import {inject, Injectable} from "@angular/core";
import {BehaviorSubject, combineLatest, EMPTY, from, Observable, of} from "rxjs";
import {distinctUntilChanged, shareReplay, switchMap} from "rxjs/operators";
import firebase from "firebase/compat/app";
import "firebase/compat/messaging";
import {UserService} from "../../../features/user/user.service";
import {DialogService} from "../../../common/dialog/dialog.service";
import {PushPermissionDialogComponent} from "../../modules/push-permission-dialog/push-permission-dialog";
import {LorenSnackBarService} from "../loren-snack-bar-service/loren-snack-bar.service";
import {LorenNotificationService} from "../loren-notification-service/loren-notification.service";
import {LorenAuthenticationService} from "../loren-authentication-service/loren-authentication.service";

const PUSH_PROMPTED_KEY = "pushPermissionPrompted";

@Injectable({providedIn: "root"})
export class LorenMessagingService {
    private readonly isSupported = firebase.messaging.isSupported();
    private readonly messaging = firebase.messaging();

    private readonly userService = inject(UserService);
    private readonly dialogService = inject(DialogService);
    private readonly snackBarService = inject(LorenSnackBarService);
    private readonly notificationService = inject(LorenNotificationService);
    private readonly authenticationService = inject(LorenAuthenticationService);

    private readonly permissionSubject = new BehaviorSubject(this.isSupported ? Notification.permission : 'denied');

    public readonly permission$ = this.permissionSubject.asObservable().pipe(distinctUntilChanged());
    public readonly token$: Observable<string | null> = this.permission$.pipe(
        switchMap(x => {
            if (x === 'granted') {
                return from(this.messaging.getToken());
            }

            return of(null);
        }),
        distinctUntilChanged(),
        shareReplay(1),
    );


    constructor() {
        // Setup
        if (this.isSupported) {
            this.messaging.onMessage((x) => {
                if (x?.data?.sound === "true") {
                    const audio = new Audio("/assets/audio/mixkit-clear-announce-tones-2861.wav");
                    audio.play();
                }
                this.snackBarService.showMessage(x.notification.body);
                this.notificationService.reloadNotificationsEvent(true);
            });
        }


        // Keep the backend in sync: register the token once we have both a token and a
        // logged-in user; deregister (post null) when the user logs out.
        combineLatest([this.token$, this.authenticationService.userId$])
            .pipe(
                switchMap(([token, userId]) => {
                    if (userId && token) {
                        return this.userService.updateUser({fcmKey: token});
                    }

                    return EMPTY;
                }),
            )
            .subscribe();

    }

    /**
     * Request a push permission from user.
     * Uses an intermediary dialog, which explains what the push notifications will be used for.
     */
    async requestPermission(force = false): Promise<void> {
        // Device does not support push notification.
        // Most often iOS Safari (not added to home screen, or iOS < 16.4)
        if (!this.isSupported) {
            return;
        }

        // Nothing to do once the user has decided — 'denied' can't be re-prompted, 'granted' is set.
        if (this.permissionSubject.value !== 'default') {
            return;
        }

        if (force) {
            await this.requestBrowserPermission();
            return;
        }

        // On iOS, removing notification permission resets Notification.permission back to
        // 'default', so the check above can't tell "never asked" from "user turned it off".
        // Remember that we've prompted so we don't nag on every login.
        if (localStorage.getItem(PUSH_PROMPTED_KEY)) {
            return;
        }
        localStorage.setItem(PUSH_PROMPTED_KEY, "true");

        this.dialogService
            .open(PushPermissionDialogComponent, {width: "420px"})
            .afterClosed()
            .subscribe((enable) => {
                if (enable) {
                    this.requestBrowserPermission();
                }
            });
    }

    private async requestBrowserPermission() {
        const permission = await Notification.requestPermission();
        this.permissionSubject.next(permission);
    }
}
